'use client';

import { useTranslation } from 'react-i18next';

import { InfiniteScrollList } from '@kit/ui/infinite-scroll-list';
import { Trans } from '@kit/ui/trans';

import type { DancerListItem } from '../_lib/server/get-dancers';
import { loadMoreDancersAction } from '../_lib/server/load-more-action';
import { DancerCard } from './dancer-card';

type LoadMoreFn = (params: {
  page: number;
  gender?: string;
  danceLevel?: string;
  search?: string;
  danceStyle?: string;
}) => Promise<{ dancers: DancerListItem[]; hasMore: boolean }>; // i18n-ignore TypeScript generic, not user-facing

interface DancersLoadMoreProps {
  initialHasMore: boolean;
  filters: {
    gender?: string;
    danceLevel?: string;
    search?: string;
    danceStyle?: string;
  };
  loadMoreAction?: LoadMoreFn;
  /** Super-admin only (BAL-392) — forwarded to infinite-scroll DancerCards
   * so later pages carry the same Edit affordance as page 0. */
  showAdminEdit?: boolean;
}

export function DancersLoadMore({
  initialHasMore,
  filters,
  loadMoreAction = loadMoreDancersAction,
  showAdminEdit = false,
}: DancersLoadMoreProps) {
  const { t } = useTranslation();
  return (
    <InfiniteScrollList<DancerListItem>
      initialHasMore={initialHasMore}
      loadMore={async (page) => {
        const result = await loadMoreAction({
          page,
          gender: filters.gender,
          danceLevel: filters.danceLevel,
          search: filters.search,
          danceStyle: filters.danceStyle,
        });
        return { items: result.dancers, hasMore: result.hasMore };
      }}
      renderItem={(dancer) => (
        <DancerCard
          key={dancer.id}
          dancer={dancer}
          showAdminEdit={showAdminEdit}
        />
      )}
      getItemKey={(dancer) => dancer.id}
      gridClassName="grid grid-cols-2 gap-x-4 gap-y-6 px-4 sm:grid-cols-3 sm:gap-x-5 sm:gap-y-8 md:grid-cols-4 lg:grid-cols-5"
      trigger="auto"
      ariaLabel={t('community:dancers.listAriaLabel')}
      loadMoreLabel={
        <Trans i18nKey="dancers:loadMore" defaults="Load more dancers" />
      }
    />
  );
}
